// Native wishlist — client-first, server-synced.
//
// Source of truth for rendering is always localStorage, so heart buttons and the
// wishlist page work instantly and never depend on network round-trips. When a
// customer id is available (window.NH_WISHLIST_CUSTOMER_ID, set best-effort in
// layout/theme.liquid — see the comment there on why this can be null even when
// logged in), we merge in the server-side copy on load and push changes back to it
// in the background. If that id is missing or a sync call fails, the wishlist still
// works locally; it just won't follow the customer to another device this session.

const STORAGE_KEY = "nh_wishlist_v1";
const PROXY_BASE = "/apps/wishlist";

function getLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

function setLocal(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage unavailable (private mode, quota, etc.) — the button still toggles
    // visually for this page view, it just won't persist. Nothing to recover here.
  }
}

function findIndex(items, productId) {
  return items.findIndex((item) => String(item.product_id) === String(productId));
}

function has(productId) {
  return findIndex(getLocal(), productId) !== -1;
}

function add(productId, variantId) {
  const items = getLocal();
  if (findIndex(items, productId) === -1) {
    items.push({ product_id: String(productId), variant_id: variantId ? String(variantId) : null, added_at: new Date().toISOString() });
    setLocal(items);
  }
  return items;
}

function remove(productId) {
  const items = getLocal().filter((item) => String(item.product_id) !== String(productId));
  setLocal(items);
  return items;
}

function toggle(productId, variantId) {
  const nowActive = !has(productId);
  const items = nowActive ? add(productId, variantId) : remove(productId);
  hydrateButtons(productId, nowActive);
  scheduleSync();
  document.dispatchEvent(new CustomEvent("nh-wishlist:change", { detail: { items } }));
  return nowActive;
}

// Updates the saved variant for an item already on the wishlist (used by the variant
// picker on the wishlist page itself) without touching its position or added_at.
function updateItemVariant(productId, variantId) {
  const items = getLocal();
  const idx = findIndex(items, productId);
  if (idx === -1) return;
  items[idx] = { ...items[idx], variant_id: variantId ? String(variantId) : null };
  setLocal(items);
  scheduleSync();
}

// Reads the variant a buy-buttons form currently has selected. Themes like this one keep
// a hidden input[name="id"] on the form in sync with the variant picker client-side (no
// reload), so this reflects whatever's showing right now, not just the page's initial load.
function getFormVariantId(formId) {
  if (!formId) return null;
  const form = document.getElementById(formId);
  const input = form && form.querySelector('input[name="id"]');
  return input ? input.value : null;
}

function hydrateButtons(productId, active) {
  const selector = productId
    ? `[data-nh-wishlist-toggle][data-product-id="${CSS.escape(String(productId))}"]`
    : "[data-nh-wishlist-toggle]";

  document.querySelectorAll(selector).forEach((btn) => {
    const isActive = productId ? active : has(btn.dataset.productId);
    btn.classList.toggle("wishlist-btn--active", isActive);
    btn.setAttribute("aria-pressed", String(isActive));
    btn.setAttribute("aria-label", isActive ? "Remove from wishlist" : "Add to wishlist");
  });
}

async function fetchServerItems() {
  const res = await fetch(`${PROXY_BASE}/get`, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`GET /get -> ${res.status}`);
  const data = await res.json();
  return Array.isArray(data.items) ? data.items : [];
}

function mergeItems(local, server) {
  const merged = new Map(local.map((item) => [String(item.product_id), item]));
  for (const item of server) {
    const key = String(item.product_id);
    if (!merged.has(key)) merged.set(key, item);
  }
  return [...merged.values()];
}

let syncTimer = null;
function scheduleSync() {
  if (!window.NH_WISHLIST_CUSTOMER_ID) return;
  clearTimeout(syncTimer);
  // Debounce so rapid toggles collapse into one request instead of one per click.
  syncTimer = setTimeout(() => {
    fetch(`${PROXY_BASE}/sync`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: getLocal() }),
    }).catch(() => {
      // Best-effort — the item is already safe in localStorage regardless.
    });
  }, 600);
}

async function mergeServerOnLoad() {
  if (!window.NH_WISHLIST_CUSTOMER_ID) return;
  try {
    const serverItems = await fetchServerItems();
    const merged = mergeItems(getLocal(), serverItems);
    setLocal(merged);
    hydrateButtons();
    // If merging pulled in anything the server didn't have (or vice versa), reconcile it.
    scheduleSync();
  } catch {
    // Logged-out-looking, network hiccup, or the known New-Customer-Accounts flakiness —
    // localStorage already reflects this page view correctly, so there's nothing to undo.
  }
}

function initButtons() {
  hydrateButtons();
  document.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-nh-wishlist-toggle]");
    if (!btn) return;
    event.preventDefault();
    const variantId = getFormVariantId(btn.dataset.variantFormId);
    toggle(btn.dataset.productId, variantId);
  });
}

// The PDP heart button sits inline with Add to Cart and should match its height exactly
// (button--xl's height itself varies responsively). `align-items: stretch` in wishlist.css
// already gets the height right; width is set here rather than via CSS `aspect-ratio`,
// which in testing sized itself off the SVG's own intrinsic width instead of the stretched
// cross size once it's a flex item alongside other flex items.
function squarePdpButtons() {
  document.querySelectorAll(".product-info__buy-buttons-row > [data-nh-wishlist-toggle]").forEach((btn) => {
    btn.style.width = `${btn.offsetHeight}px`;
  });
}

function init() {
  initButtons();
  mergeServerOnLoad();
  squarePdpButtons();
  window.addEventListener("resize", debounce(squarePdpButtons, 150));

  const page = document.querySelector("[data-nh-wishlist-page]");
  if (page) initWishlistPage(page);
}

function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

// --- Wishlist page rendering -------------------------------------------------------
// Renders client-side from localStorage + the Worker's /products lookup, since the
// page's own liquid render happens before we know what's in localStorage.

async function initWishlistPage(container) {
  document.addEventListener("nh-wishlist:change", () => renderWishlistPage(container));
  container.addEventListener("change", (event) => {
    const select = event.target.closest("[data-nh-variant-select]");
    if (select) onVariantChange(select);
  });
  container.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-nh-add-to-cart]");
    if (btn) onAddToCart(btn);
  });
  await renderWishlistPage(container);
}

async function renderWishlistPage(container) {
  const items = getLocal();

  // Every re-render here is triggered by removing an item (the only toggle action available
  // on this page), which replaces the whole grid and would otherwise drop keyboard focus back
  // to <body>. Capture which remove button (by position) had focus beforehand so it can be
  // restored afterward — to the same slot's button, the last button if that slot no longer
  // exists, or the empty-state message if nothing is left.
  const removeButtons = [...container.querySelectorAll(".wishlist-page__remove")];
  const focusedIndex = removeButtons.indexOf(document.activeElement);
  const hadFocus = focusedIndex !== -1;

  if (items.length === 0) {
    container.innerHTML = `<p class="wishlist-page__empty" tabindex="-1">${container.dataset.emptyText || "Your wishlist is empty."}</p>`;
    if (hadFocus) container.querySelector(".wishlist-page__empty").focus();
    return;
  }

  container.setAttribute("aria-busy", "true");

  try {
    const res = await fetch(`${PROXY_BASE}/products`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_ids: items.map((item) => item.product_id) }),
    });
    if (!res.ok) throw new Error(`POST /products -> ${res.status}`);
    const { products } = await res.json();

    if (!products.length) {
      container.innerHTML = `<p class="wishlist-page__empty" tabindex="-1">${container.dataset.emptyText || "Your wishlist is empty."}</p>`;
      if (hadFocus) container.querySelector(".wishlist-page__empty").focus();
      return;
    }

    const savedVariantByProduct = new Map(items.map((item) => [String(item.product_id), item.variant_id]));
    container.innerHTML = products.map((product) => renderWishlistCard(product, savedVariantByProduct.get(String(product.product_id)))).join("");

    if (hadFocus) {
      const newButtons = container.querySelectorAll(".wishlist-page__remove");
      const target = newButtons[Math.min(focusedIndex, newButtons.length - 1)];
      if (target) target.focus();
    }
  } catch {
    container.innerHTML = `<p class="wishlist-page__error" tabindex="-1">${container.dataset.errorText || "Couldn't load your wishlist right now — try refreshing."}</p>`;
    if (hadFocus) container.querySelector(".wishlist-page__error").focus();
  } finally {
    container.removeAttribute("aria-busy");
  }
}

function pickInitialVariant(product, savedVariantId) {
  const variants = product.variants || [];
  const saved = savedVariantId && variants.find((v) => v.variant_id === String(savedVariantId));
  if (saved) return saved;
  return variants.find((v) => v.available) || variants[0] || null;
}

function formatMoney(amount, currency) {
  if (amount == null) return "";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(amount);
}

function renderWishlistCard(product, savedVariantId) {
  const variants = product.variants || [];
  const initial = pickInitialVariant(product, savedVariantId);
  const hasChoices = variants.length > 1;

  const priceHtml = formatMoney(initial ? initial.price : product.price, product.currency);

  const selectHtml = hasChoices
    ? `
      <div class="relative w-full">
        <select class="select wishlist-page__variant-select" data-nh-variant-select data-product-id="${product.product_id}" aria-label="Select a variant">
          ${variants
            .map(
              (v) =>
                `<option value="${v.variant_id}" data-price="${escapeAttr(formatMoney(v.price, product.currency))}" ${v.variant_id === (initial && initial.variant_id) ? "selected" : ""} ${v.available ? "" : "disabled"}>${escapeHtml(v.title)}${v.available ? "" : " - Sold out"}</option>`
            )
            .join("")}
        </select>
        <span class="select-chevron wishlist-page__select-chevron" aria-hidden="true">
          <svg viewBox="0 0 10 6" width="10" height="6" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M1 1l4 4 4-4"/></svg>
        </span>
      </div>
    `
    : "";

  const addToCartDisabled = !initial || !initial.available;

  return `
    <div class="product-card product-card--blends wishlist-page__card" data-nh-wishlist-card data-product-id="${product.product_id}">
      <div class="product-card__figure">
        <a href="/products/${product.handle}" data-instant>
          ${product.image_url ? `<img class="product-card__image product-card__image--primary" src="${escapeAttr(product.image_url)}" alt="${escapeAttr(product.image_alt)}" loading="lazy">` : ""}
        </a>
        <button type="button" class="wishlist-btn wishlist-btn--active wishlist-page__remove" data-nh-wishlist-toggle data-product-id="${product.product_id}" aria-label="Remove from wishlist">
          <svg viewBox="0 0 448 512" width="18" height="20" fill="currentColor" aria-hidden="true">
            <path d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3zM32 128l384 0 0 320c0 35.3-28.7 64-64 64L96 512c-35.3 0-64-28.7-64-64l0-320zm96 64c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16z"/>
          </svg>
        </button>
      </div>
      <div class="product-card__info">
        <span class="product-card__title"><a href="/products/${product.handle}" data-instant>${escapeHtml(product.title)}</a></span>
        <span class="wishlist-page__price" data-nh-price>${priceHtml}</span>
      </div>

      <div class="wishlist-page__controls">
        ${selectHtml}

        ${
          addToCartDisabled
            ? ""
            : `
          <quantity-selector class="quantity-selector wishlist-page__quantity-selector">
            <button type="button" tabindex="-1" class="quantity-selector__button" aria-label="Decrease quantity">
              <svg role="presentation" focusable="false" width="10" height="2" viewBox="0 0 10 2"><path d="M0 0H10V2H0V0Z" fill="currentColor"></path></svg>
            </button>
            <input
              type="number"
              is="quantity-input"
              inputmode="numeric"
              class="quantity-selector__input wishlist-page__quantity-input"
              data-nh-quantity
              value="1"
              step="1"
              min="1"
              autocomplete="off"
              aria-label="Quantity"
            >
            <button type="button" tabindex="-1" class="quantity-selector__button" aria-label="Increase quantity">
              <svg role="presentation" focusable="false" stroke-width="2" width="12" height="12" viewBox="0 0 12 12"><path d="M6 0V12" fill="none" stroke="currentColor"></path><path d="M0 6L12 6" fill="none" stroke="currentColor"></path></svg>
            </button>
          </quantity-selector>
        `
        }

        <button
          type="button"
          class="button button--sm w-full wishlist-page__add-to-cart"
          data-nh-add-to-cart
          data-product-id="${product.product_id}"
          data-variant-id="${initial ? initial.variant_id : ""}"
          ${addToCartDisabled ? "disabled" : ""}
        >
          ${addToCartDisabled ? "Sold out" : "Add to cart"}
        </button>
      </div>
    </div>
  `;
}

function onVariantChange(select) {
  const productId = select.dataset.productId;
  const variantId = select.value;
  const card = select.closest("[data-nh-wishlist-card]");
  const selectedOption = select.selectedOptions[0];

  updateItemVariant(productId, variantId);

  const addToCartBtn = card.querySelector("[data-nh-add-to-cart]");
  const available = selectedOption && !selectedOption.disabled;
  addToCartBtn.dataset.variantId = variantId;
  addToCartBtn.disabled = !available;
  addToCartBtn.textContent = available ? "Add to cart" : "Sold out";

  const priceEl = card.querySelector("[data-nh-price]");
  if (priceEl) priceEl.textContent = selectedOption.dataset.price || "";
}

async function onAddToCart(btn) {
  const variantId = btn.dataset.variantId;
  if (!variantId || btn.disabled) return;

  const card = btn.closest("[data-nh-wishlist-card]");
  const quantityInput = card && card.querySelector("[data-nh-quantity]");
  const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);

  const originalText = btn.textContent;
  btn.disabled = true;
  btn.textContent = "Adding…";

  try {
    // A plain POST to /cart/add.js adds the item, but the theme's own cart drawer (assets/theme.js,
    // CartDrawer) never learns it happened — it only opens/refreshes in response to specific bubbling
    // events carrying pre-rendered section HTML, exactly as the theme's own product-form submit handler
    // dispatches them. Replicating that same sequence here is what makes the drawer open and show the
    // new item immediately, instead of the cart only updating silently in the background.
    const sectionsToBundle = ["variant-added"];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const formData = new FormData();
    formData.set("id", variantId);
    formData.set("quantity", quantity);
    formData.set("sections", sectionsToBundle.join(","));
    formData.set("sections_url", `${Shopify.routes.root}variants/${variantId}`);

    const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      body: formData,
    });

    const responseJson = await res.json();
    if (!res.ok) {
      throw new Error(responseJson?.description || `Cart add failed (${res.status})`);
    }

    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cartContent.sections = responseJson.sections;

    document.dispatchEvent(
      new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty("items") ? responseJson.items : [responseJson],
          cart: cartContent,
        },
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart: cartContent } })
    );

    btn.textContent = "Added ✓";
  } catch {
    btn.textContent = "Couldn't add — retry";
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  } finally {
    setTimeout(() => {
      btn.textContent = originalText;
      btn.disabled = false;
    }, 1800);
  }
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function escapeAttr(str) {
  return escapeHtml(str);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
